import { useCallback } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Button } from '@antimatterhq/ui/components/button';
import Spacer from '@antimatterhq/ui/components/spacer';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { Section } from '@/components/container/section/Section';
import { SilverDataSourceSchemaValues } from '@/zod/schemas/dataSource/Silver';
import { AddTableSchemaValues } from '@/zod/schemas/dataSource/Common';
import { AddTable } from '@/components/dataSources/addTable/AddTable';
import { TocWrapper } from '@/components/tableOfContents/ToCWrapper';
import { SilverDataPreviewContainer } from '@/components/dataSources/dataPreviewContainer/silver/SilverDataPreviewContainer';

const emptyAddTable: AddTableSchemaValues = {
  name: '',
  customFunction: '',
  customFunctionOptions: [],
  filter: '',
  postFilter: '',
  fields: [],
  jsonExtract: [],
  preserve: false,
  omitColumns: [''],
  duplicatePrefix: '',
  embedColumn: '',
  embed: false,
  enableOverrideLiquidColumns: false,
  overrideLiquidColumns: [''],
  tableMode: 'configure',
  temporaryFields: [],
};

export const SilverAddTables = () => {
  const formik = useFormikContext<SilverDataSourceSchemaValues>();
  const { enableDatasourcePreview } = useFlags<FlagSet>();

  const { values } = formik;

  const createAddTable = useCallback(() => ({ ...emptyAddTable }), []);

  return (
    <div>
      <FieldArray name="addTables">
        {({ remove, push }) => (
          <>
            <Button
              leftIcon="radix-icons:plus"
              variant="default"
              className="w-fit"
              onClick={() => push(createAddTable())}
              type="button"
              data-testid="add-table-button-top"
            >
              Add a table
            </Button>

            {!!values.addTables.length && <Spacer size="lg" />}

            {values.addTables.map((addTable, index) => {
              const prefix = `addTables.${index}.`;
              return (
                <div key={prefix}>
                  <TocWrapper
                    name={`addTables.${index}`}
                    displayName={addTable.name || `Table ${index + 1}`}
                  >
                    {!!index && <Spacer size="lg" />}
                    <Section className="relative">
                      <div className="absolute right-5 top-5 flex items-center gap-2">
                        {enableDatasourcePreview && (
                          <SilverDataPreviewContainer
                            tableIndex={index}
                            isModifyTable={false}
                          />
                        )}
                        <Button
                          variant="ghost"
                          size="small"
                          iconOnly
                          leftIcon="antimatterhq:trash"
                          onClick={() => remove(index)}
                        />
                      </div>

                      <AddTable
                        index={index}
                        currentTable="silver"
                        previousTable="bronze"
                        table={addTable}
                        enableClustering={true}
                      />
                    </Section>
                  </TocWrapper>
                </div>
              );
            })}

            {!!values.addTables.length && (
              <>
                <Spacer size="lg" />

                <Button
                  leftIcon="radix-icons:plus"
                  variant="default"
                  className="w-fit"
                  onClick={() => push(createAddTable())}
                  type="button"
                  data-testid="add-table-button-bottom"
                >
                  Add a table
                </Button>
              </>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );
};
